import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ListTextEditCell } from '../util/DataTableCells';
import { validateEmails } from '../../utils/parsing';

const parseEmails = (raw) => raw.map((e) => e.trim()).filter(Boolean);

const validateThreshold = (value) => {
  const num = Number(value);
  if (value === '' || Number.isNaN(num)) {
    return 'Threshold is required';
  }
  if (num < 1 || num > 100) {
    return 'Threshold must be between 1 and 100';
  }
  return undefined;
};

const validateInterval = (value, required = true) => {
  if (!value) return required ? 'This field is required' : undefined;
  const num = Number(value);
  if (Number.isNaN(num)) return 'Must be a number';
  if (num < 60) return 'Must be at least 60 minutes';
  return undefined;
};

const INITIAL_FORM = {
  threshold: 50,
  name: '',
  initial: '',
  realert: '',
  clear: '',
  emails: [],
};

const AlertCreationDialog = ({ buildings, equipment, onSubmit, onCancel }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({ realert: false, clear: false });
  const [errors, setErrors] = useState({});

  const resetState = () => {
    setForm(INITIAL_FORM);
    setTouched({ realert: false, clear: false });
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onCancel();
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;

    setForm((prev) => ({ ...prev, [field]: value }));

    if (field === 'realert' || field === 'clear') {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }

    if (field === 'threshold') {
      setErrors((prev) => ({
        ...prev,
        threshold: validateThreshold(value),
      }));
    }

    if (field === 'emails') {
      setErrors((prev) => ({
        ...prev,
        emails: validateEmails(parseEmails(value)),
      }));
    }

    if (field === 'initial') {
      setErrors((prev) => ({ ...prev, initial: validateInterval(value) }));
    }
    if (field === 'realert' || field === 'clear') {
      setErrors((prev) => ({ ...prev, [field]: validateInterval(value, false) }));
    }
    if (field === 'name') {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const realertValue = touched.realert ? form.realert : form.initial;
  const clearValue = touched.clear ? form.clear : form.initial;
  const { threshold } = form;

  const handleSubmit = (event) => {
    event.preventDefault();

    const validationErrors = {
      initial: validateInterval(form.initial),
      realert: validateInterval(realertValue, false),
      clear: validateInterval(clearValue, false),
      threshold: validateThreshold(form.threshold),
      name: form.name?.trim() ? undefined : 'Alert name is required',
      emails: validateEmails(form.emails),
    };

    if (Object.values(validationErrors).some(Boolean)) {
      setErrors(validationErrors);
      return;
    }

    onSubmit({
      ...form,
      enabled: true,
      buildings: buildings?.map((b) => b.id),
      equipment: equipment?.map((e) => e.id),
      percent_threshold: threshold / 100,
      alert_initial_interval: Number(form.initial) * 60,
      alert_realert_interval: Number(realertValue) * 60,
      alert_clear_interval: Number(clearValue) * 60,
      alert_emails: parseEmails(form.emails),
    });

    resetState();
  };

  const alertMessage =
    buildings?.length > 0
      ? `Creating alert for [${buildings?.length}] Buildings`
      : `Creating alert for [${equipment?.length}] Equipment`;
  const pointCount =
    buildings?.length > 0
      ? buildings.map((b) => b.pointCount).reduce((acc, cur) => acc + cur, 0)
      : equipment
          ?.map(({ pointIds }) => new Set(pointIds))
          .reduce((acc, cur) => acc.union(cur), new Set()).size;
  const impactedPoints = `(${pointCount} points)`;
  return (
    <Dialog open={equipment?.length > 0 || buildings?.length > 0} fullWidth onClose={handleClose}>
      <DialogTitle>
        {alertMessage} {impactedPoints}
      </DialogTitle>
      <form onSubmit={handleSubmit} onReset={handleClose} id="alert-creation">
        <DialogContent>
          <FormControl fullWidth>
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="% threshold"
              name="threshold"
              type="number"
              value={form.threshold}
              onChange={handleChange('threshold')}
              error={!!errors.threshold}
              helperText={errors.threshold}
              autoFocus
              InputProps={{
                inputProps: { min: 1, max: 100 },
              }}
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="name"
              name="name"
              value={form.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name ?? 'A descriptive name for this alert'}
              placeholder="Alert name"
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="initial interval minutes"
              name="initial"
              type="number"
              value={form.initial}
              onChange={handleChange('initial')}
              error={!!errors.initial}
              helperText={
                errors.initial ??
                'Required — the alert is triggered based on this initial time range'
              }
              placeholder="The alert is triggered based on the initial time range"
              InputProps={{ inputProps: { min: 60 } }}
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="realert interval minutes"
              name="realert"
              type="number"
              value={realertValue}
              onChange={handleChange('realert')}
              error={!!errors.realert}
              helperText={errors.realert ?? 'Defaults to the initial interval'}
              placeholder="The alert will fire again based on the time interval."
              InputProps={{ inputProps: { min: 60 } }}
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="clear interval minutes"
              name="clear"
              type="number"
              value={clearValue}
              onChange={handleChange('clear')}
              error={!!errors.clear}
              helperText={errors.clear ?? 'Defaults to the initial interval'}
              placeholder="The alert is considered cleared if it is resolved for this interval."
              InputProps={{ inputProps: { min: 60 } }}
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              disabled
              value="Emails:"
              error={!!errors.emails}
              helperText={errors.emails ?? 'Alert Emails'}
            />
            <ListTextEditCell
              value={form.emails}
              validator={(e, idx) => (idx !== 0 && e === '') || validateEmails([e])}
              update={(newValue) => handleChange('emails')({ target: { value: newValue } })}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button type="reset" form="alert-creation">
            Cancel
          </Button>
          <Button variant="contained" color="success" type="submit" form="alert-creation">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

AlertCreationDialog.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  buildings: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  equipment: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
AlertCreationDialog.defaultProps = {
  buildings: [],
  equipment: [],
};

export default AlertCreationDialog;
